import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { AppMenuItem } from '../../util/menu-item';

/**
 * A context menu at the mouse pointer: `[model]` and `show(event)`. It is a
 * mat-menu like the other popup menus, opened from an invisible trigger at the
 * pointer, so the CDK moves it into the window. It closes on a click next to
 * it, a right click elsewhere and a resize of the window, without a backdrop in
 * the way of the next click.
 */
@Component({
  selector: 'app-context-menu',
  standalone: true,
  imports: [MatMenuModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div
      class="app-context-menu-trigger"
      aria-hidden="true"
      [style.left.px]="x"
      [style.top.px]="y"
      [matMenuTriggerFor]="menu"
      [matMenuTriggerRestoreFocus]="false"
    ></div>
    <mat-menu
      #menu="matMenu"
      class="app-menu app-context-menu {{ styleClass }}"
      [hasBackdrop]="false"
      (closed)="onClosed()"
    >
      @for (item of model; track $index) {
        <button
          mat-menu-item
          type="button"
          [disabled]="item.disabled"
          (click)="item.command?.()"
        >
          @if (item.icon) {
            <span class="app-context-menu-icon {{ item.icon }}"></span>
          }
          <span class="app-context-menu-text">{{ item.label }}</span>
        </button>
      }
    </mat-menu>`,
  styles: [
    `
      .app-context-menu-trigger {
        position: fixed;
        width: 0;
        height: 0;
        pointer-events: none;
      }
    `,
  ],
})
export class ContextMenuComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private document = inject(DOCUMENT);

  @Input() model: AppMenuItem[] = [];
  @Input() styleClass = '';
  @Output() hidden = new EventEmitter<void>();

  @ViewChild(MatMenuTrigger, { static: true })
  private trigger!: MatMenuTrigger;

  x = 0;
  y = 0;
  private unlisten: (() => void)[] = [];

  get visible() {
    return this.trigger.menuOpen;
  }

  /** Opens the menu at the pointer of a (contextmenu) event. */
  show(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (this.trigger.menuOpen) {
      this.trigger.closeMenu();
    }
    this.x = event.clientX + 1;
    this.y = event.clientY + 1;
    this.cdr.detectChanges();
    this.trigger.openMenu();
    this.listen();
  }

  hide() {
    this.trigger.closeMenu();
  }

  ngOnDestroy(): void {
    this.stopListening();
  }

  onClosed() {
    // Reopening at another place closes the menu first.
    if (!this.trigger.menuOpen) {
      this.stopListening();
    }
    this.hidden.emit();
  }

  private listen() {
    this.stopListening();
    const view = this.document.defaultView;
    const mousedown = (event: Event) => {
      if (!(event.target as Element | null)?.closest?.('.app-context-menu')) {
        this.hide();
      }
    };
    // A context menu event that opens a menu stops here, see show().
    const other = () => this.hide();
    this.document.addEventListener('mousedown', mousedown, true);
    this.document.addEventListener('contextmenu', other);
    view?.addEventListener('resize', other);
    this.unlisten = [
      () => this.document.removeEventListener('mousedown', mousedown, true),
      () => this.document.removeEventListener('contextmenu', other),
      () => view?.removeEventListener('resize', other),
    ];
  }

  private stopListening() {
    this.unlisten.forEach((remove) => remove());
    this.unlisten = [];
  }
}
